import type { ReactNode } from "react";

export interface AdminListDetailLayoutProps {
  /** 페이지 제목 */
  title: string;
  /** 페이지 설명 (h1 아래) */
  description: string;
  /** 왼쪽 패널 비율 (기본 1) */
  leftRatio?: number;
  /** 오른쪽 패널 비율 (기본 1) */
  rightRatio?: number;
  /** 왼쪽 패널 */
  leftPanel: {
    title: string;
    headerAction?: ReactNode;
    searchPlaceholder?: string;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    children: ReactNode;
  };
  /** 오른쪽 패널 */
  rightPanel: {
    title: string;
    /** 선택 없을 때 표시 (기본: "왼쪽 목록에서 매장을 선택하세요.") */
    emptyText?: string;
    /** null이면 emptyText 표시 */
    children: ReactNode | null;
  };
  /** 제목·설명 아래, 그리드 위에 표시할 추가 콘텐츠 */
  headerExtra?: ReactNode;
}

/** 오른쪽(상세/액션) 패널 id - 모바일 단일 선택 후 스크롤 타깃용 */
export const ADMIN_LIST_DETAIL_RIGHT_PANEL_ID = "admin-list-detail-right-panel";

const DEFAULT_EMPTY_TEXT = "왼쪽 목록에서 매장을 선택하세요.";

export function AdminListDetailLayout({
  title,
  description,
  leftRatio = 1,
  rightRatio = 1,
  leftPanel,
  rightPanel,
  headerExtra,
}: AdminListDetailLayoutProps) {
  const hasSearch =
    leftPanel.searchValue !== undefined &&
    leftPanel.onSearchChange !== undefined;

  const gridColsClass =
    leftRatio === 7 && rightRatio === 3
      ? "lg:grid-cols-[7fr_3fr]"
      : "lg:grid-cols-2";

  return (
    <div className="w-full md:p-8">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">{title}</h1>
      <p className="text-gray-600 text-sm mb-6">{description}</p>
      {headerExtra ? <div className="mb-6">{headerExtra}</div> : null}

      <div className={`grid grid-cols-1 gap-6 ${gridColsClass} items-start`}>
        {/* 왼쪽: 목록 패널 */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={
              hasSearch
                ? "px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-3"
                : "px-4 py-3 border-b border-gray-200 bg-gray-50"
            }
          >
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {leftPanel.title}
              </h2>
              {leftPanel.headerAction}
            </div>
            {hasSearch && (
              <input
                type="text"
                value={leftPanel.searchValue}
                onChange={(e) => leftPanel.onSearchChange?.(e.target.value)}
                placeholder={leftPanel.searchPlaceholder ?? "매장명으로 검색"}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>
          <div className="min-h-96 max-h-[calc(100vh-14rem)] overflow-y-auto">
            {leftPanel.children}
          </div>
        </div>

        {/* 오른쪽: 상세 패널 (모바일 단일 선택 시 스크롤 타깃) */}
        <div
          id={ADMIN_LIST_DETAIL_RIGHT_PANEL_ID}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">
              {rightPanel.title}
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {rightPanel.children ?? (
              <p className="text-gray-500 text-sm">
                {rightPanel.emptyText ?? DEFAULT_EMPTY_TEXT}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
